import Stomp from '@stomp/stompjs';
import { all, call, delay, put, race, select, spawn, take, takeEvery } from 'redux-saga/effects';
import SockJS from 'sockjs-client';
import { map } from 'ramda';
import { eventChannel } from 'redux-saga';
import shortid from 'shortid';
import * as HttpStatus from 'http-status-codes';

import * as apiListener from './apiListener';
import * as chatListener from './chatListener';
import * as eventListener from './eventListener';
import { channelHandler, channelRead } from './channelListener';

import * as endpoints from '../constants/ApiEndpoints';
import * as actions from '../actions/chatActions';

import * as types from '../constants/ActionTypes';

import { store } from '../index';
import * as socketActions from '../middleware/socketActions';
import * as socketStatus from '../constants/SocketStatus';
import * as modalSaga from './modalSaga';
import * as pdfSaga from './pdfSaga';
import * as sendSagas from './sendSagas';
import * as getChatsSagas from './getChatsSagas';
import * as messageSendSagas from './messageSendSagas';
import { messageReceivedSaga } from './messageReceivedSaga';
import { syncData } from './syncDataSaga';

import { _postMessage, logError, logWarning } from '../helpers/BrowserUtils';
import { getTimeoutLength } from './generatorUtils';
import { hasAuth, setAuth } from '../helpers/AuthUtils';
import { getLocalMemberId, isSameMember } from '../helpers/UserUtils';
import { getCsrfToken } from '../helpers/BrowserUtils';

import {
  FAIL_SUFFIX,
  HEARTBEAT_PERIOD,
  LOGGED_IN,
  LOGGING_OUT,
  SUCCESS_SUFFIX,
  TIMEOUT_SUFFIX,
} from '../constants/AppConstants';
import { get } from 'object-path';
import { addUserToSentryScope } from '../helpers/SentryUtils';

let stompClient;
let reconnectAttempts = 0;
const MAX_NUM_RECONNECT_RETRIES = 5;

// Force the use of websocket transport by having be the only protocol
const sockJsProtocols = ['websocket'];

const actionEndpoints = {
  [types.SEARCH_USERS]: endpoints.USER_SEARCH_V1,
  [types.GET_CHATS]: endpoints.CHAT_GET_V1,
  [types.GET_ALL_CHATS]: endpoints.CHAT_GET_ALL_V1,
  [types.GET_PUBLIC_CHANNELS]: endpoints.CHAT_GET_PUBLIC_CHANNELS_V1,
  [types.GET_ATTACHMENT]: endpoints.ATTACHMENT_GET_V1,
  [types.START_DM]: endpoints.DM_START_V1,
  [types.START_CHANNEL]: endpoints.CHANNEL_START_V1,
  [types.HIDE_CHAT]: endpoints.HIDE_CHAT_V1,
  [types.SHOW_CHAT]: endpoints.HIDE_CHAT_V1,
  [types.GET_IDENTITY]: endpoints.IDENTITY_GET_V1,
  [types.ADD_MESSAGE]: endpoints.CHAT_SEND_V1,
  [types.GET_MESSAGES]: endpoints.MESSAGE_GET_V1,
  [types.MARK_AS_READ]: endpoints.MARK_AS_READ_V1,
  [types.GET_UNREAD_COUNT]: endpoints.GET_UNREAD_COUNT_V1,
  [types.JOIN_CHANNEL]: endpoints.CHANNEL_JOIN_V1,
  [types.LEAVE_CHANNEL]: endpoints.CHANNEL_LEAVE_V1,
  [types.ADD_USERS_TO_CHANNEL]: endpoints.CHANNEL_ADD_USERS_V1,
  [types.SET_TYPING]: endpoints.CHAT_SET_TYPING_V1,
  [types.WEBSOCKET_PING]: endpoints.WEBSOCKET_PING_V1,
};

function* awaitResponse(id) {
  while (true) {
    const response = yield take(types.API_RESPONSE_RECEIVED);
    const { headers: { 'request-id': requestId }, body } = response.payload;
    if (requestId === id) {
      return JSON.parse(body);
    }
  }
}

/**
 * Function gen that sends websocket frames
 * @param type - action type
 * @param payload - message
 * @param numRetries - num of retries for api req
 * @returns {IterableIterator<*>}
 */
export function* send({ type, payload }, numRetries = 0) {
  let n = 0;
  while (n++ <= numRetries) {
    try {
      const id = yield call(shortid.generate);
      stompClient.send(actionEndpoints[type], { 'request-id': id }, JSON.stringify(payload));
      const timeoutLength = getTimeoutLength(type);
      // If the ApiResponse isn't received before 'timeoutLength', then we timeout the call to avoid infinite hanging
      const { response } = yield race({
        response: call(awaitResponse, id),
        timeout: delay(timeoutLength),
      });

      if (!response) {
        if (n < numRetries) {
          console.log(`send: ${type}, attempt = ${n} - timed out`);
          continue;
        } else {
          logError(`Request [${type}] Timed out. Request-id: ${id}.  Attempt ${n}`);
        }
        return { successful: false, reason: 'Timeout', type: `${type}_${TIMEOUT_SUFFIX}` };
      }
      // Add the response type so the callers don't duplicate this logic
      response['type'] = response.successful ? `${type}_${SUCCESS_SUFFIX}` : `${type}_${FAIL_SUFFIX}`;
      return response;
    } catch (error) {
      logError(`Websocket.send -> ${type} failed. Error: `, error);
    }
  }
}

/**
 *  Exponentially back-off and stagger how often we retry socket connection, in milliseconds
 *  In seconds, half of [2, 4, 8, 16, 32] + 1 to 3
 */
const getTimeoutByReconnectAttempt = attempt => (Math.pow(2, attempt) + getRandomNumberBetween(1, 3)) * 500;
const getRandomNumberBetween = (min, max) => (Math.random() * (max - min)) + min;

const stompConnectWithPromise = (stompClient, socketState) => new Promise((resolve, reject) => {
  stompClient.debug = function () {};
  stompClient.heartbeat.outgoing = HEARTBEAT_PERIOD; // client will send heartbeats every X milliseconds
  stompClient.heartbeat.incoming = HEARTBEAT_PERIOD; // client to receive heartbeats from server (0 = no heartbeat)

  stompClient.connect({ 'X-XSRF-TOKEN': getCsrfToken() },
    (frame) => {
      if (![socketStatus.SOCKET_UNESTABLISHED, socketStatus.SOCKET_CONNECTED].includes(socketState)) {
        store.dispatch({ type: types.SYNC_DATA });
      }

      resolve(frame);
    },
    (err) => {
      store.dispatch(socketActions.socketConnectFailed());

      if (reconnectAttempts < MAX_NUM_RECONNECT_RETRIES) {
        reconnectAttempts++;
        const timeout = getTimeoutByReconnectAttempt(reconnectAttempts);
        setTimeout(() => store.dispatch(socketActions.socketReconnect()), timeout);
      } else {
        logError('Reached maximum amount of reconnect retries.  Please check network connection.', err);
        store.dispatch(socketActions.maximumSocketReconnectAttemptsReached(true));
      }

      reject(err);
    });
});

const createSocketChannel = (queueEndpoint, responseHandler) => eventChannel((emit) => {
  const messageHandler = message => responseHandler(emit, message);

  // Create queue subscription
  const subscription = stompClient.subscribe(queueEndpoint, messageHandler);

  // Return the unsubscribe function
  return () => subscription.unsubscribe();
});

function* startListener({ endpoint, handler, read }) {
  const channel = yield call(createSocketChannel, endpoint, handler);
  yield spawn(read, channel);
}

export function* startChannelListener(endpoint) {
  const channel = yield call(createSocketChannel, endpoint, channelHandler);
  yield spawn(channelRead, channel);
}

/**
 * Notification to parent window (Inbox) that messenger iFrame has loaded
 * This particular message is used as a blocking mechanism for Inbox to wait for
 * Messenger to be loaded before it attempts to log out.
 *
 * TODO: This will be removed once Central Auth is implemented...
 */
function appStartDataComplete() {
  const isIFrame = window !== window.parent;
  if (isIFrame) {
    const parentWindow = window.parent;
    parentWindow && _postMessage(parentWindow, { eventName: 'messenger-loaded' }, '*');
  }
}

/**
 * The necessary app start data includes:
 * 1) Get all of the chats the user belongs to
 * 2) Get all public channels that a user can join
 * 3) Get all the users this user can talk to
 */
function* getAppStartData() {
  const numRetries = 5;
  const responses = yield all([
    call(getChatsSagas.getAllChatsSaga, actions.getAllChats(), numRetries),
    call(sendSagas.getPublicChannelsSaga, actions.getPublicChannels(), numRetries),
    call(sendSagas.searchUsersSaga, actions.searchUsers(), numRetries),
  ]);

  if (responses.some(resp => resp === false)) {
    // one or more app start data call failed
    // todo : put({ type: APP START DATA FAILED {) ... // triggers a toast
    // todo : showing the user that Messenger may not be working properly
    // todo : we should also proactively try to get the data back into good state
    logError('Failed to get all app start data:', responses);
  }

  yield call(appStartDataComplete);
}

// Listens for and catches events that involve async requests
function* rootListener() {
  yield all([
    takeEvery(types.ADD_USER_CONTEXT_TO_SENTRY, addUserToSentryScope),
    takeEvery(types.GET_APP_START_DATA, getAppStartData),
    takeEvery(types.SOCKET_CONNECTING, socketConnect),
    takeEvery(types.SOCKET_RECONNECT, reconnect),
    takeEvery(types.SYNC_DATA, syncData),
    takeEvery(types.SEARCH_USERS, sendSagas.searchUsersSaga),
    takeEvery(types.GET_ALL_CHATS, getChatsSagas.getAllChatsSaga),
    takeEvery(types.GET_CHATS, getChatsSagas.getChatsSaga),
    takeEvery(types.GET_PUBLIC_CHANNELS, sendSagas.getPublicChannelsSaga),
    takeEvery([types.START_DM, types.START_CHANNEL], sendSagas.startChatSaga),
    takeEvery([types.HIDE_CHAT, types.SHOW_CHAT], sendSagas.hideShowChatSaga),
    takeEvery(types.ADD_MESSAGE, messageSendSagas.addMessageSaga),
    takeEvery(types.RETRY_MESSAGE_FAIL, messageSendSagas.retryMessageFailSaga),
    takeEvery(types.DELETE_MESSAGE_FAIL, messageSendSagas.deleteMessageFailSaga),
    takeEvery(types.GET_MESSAGES, sendSagas.getMessagesSaga),
    takeEvery(types.GET_ATTACHMENT, sendSagas.getAttachmentSaga),
    takeEvery(types.JOIN_CHANNEL, sendSagas.joinChannelSaga),
    takeEvery(types.LEAVE_CHANNEL, sendSagas.leaveChannelSaga),
    takeEvery(types.ADD_USERS_TO_CHANNEL, sendSagas.addUsersToChannelSaga),
    takeEvery(types.MARK_AS_READ, sendSagas.markAsReadSaga),
    takeEvery(types.MESSAGE_RECEIVED, messageReceivedSaga),
    takeEvery(types.GET_UNREAD_COUNT, sendSagas.getUnreadCountSaga),
    takeEvery(types.DOWNLOAD_ATTACHMENT, sendSagas.downloadAttachment),
    takeEvery(types.DOWNLOAD_VIDEO_IMAGE_ATTACHMENT, sendSagas.downloadVideoAttachment),
    takeEvery(types.SET_TYPING, sendSagas.setTypingSaga),
    takeEvery(types.PDF_EXPORT_MESSAGES, pdfSaga.buildAndSendPdfExport),
    takeEvery(types.PDF_SEND_TO_INBOX, pdfSaga.sendPdfExport),
    takeEvery(types.MODAL_CLOSE, modalSaga.closeModalSaga),
    takeEvery(types.MODAL_UPDATE, modalSaga.updateModalSaga),
    takeEvery(types.LOGOUT, logout),
  ]);
}

function logout() {
  /**
   * The OAuth flow is going to bring us back to Messenger UI at some point and we would
   * like to know that we are in the process of logging out so we can act accordingly.
   */
  setAuth(LOGGING_OUT);
  window.location.replace(endpoints.LOGOUT);
}

function* reconnect() {
  const socketState = yield select(state => state.socket.status);
  // Check to see if the user is a webapp user : a) iFrame within webapp or,
  // b) signed into webapp and then went to chat.domain.com (still authenticated via webapp)
  const isWebappUser = yield select(state => state.identity.isWebappUser);
  if (socketState === socketStatus.SOCKET_DISCONNECTED) {
    if (hasAuth(LOGGED_IN)) {
      yield put(socketActions.socketConnect(isWebappUser));
    } else {
      // the SSO session might still be active even if the local session has timed out,
      // so just kill the local session and try to re-SSO when the iframe reloads
      window.location.replace(endpoints.LOCAL_LOGOUT);
    }
  }
}

/**
 * In the event of a websocket disconnect but not a refresh, we want to be sure to reconnect
 * to our channels (topic subscriptions)
 */
/* function* subscribeToChannels() {
  const chats = yield select(state => state.chats);

  if (!isEmpty(chats)) {
    const chatArr = Object.values(chats);
    const endpoints = buildSubscriptionEndpoints(chatArr);
    if (endpoints.length > 0) {
      yield all(map(startChannelListener, endpoints));
    }
  }
}*/

/**
 * @param action - Socket action object.  The payload we care about is 'isWebappUser' -> users authenticated via webapp
 * have different app configurations (security) than all other clients.
 */
function* socketConnect(action) {
  try {
    const socketState = yield select(state => state.socket.status);
    stompClient = yield call(Stomp.over, new SockJS(endpoints.WEBSOCKET_CONNECT_V1, null, { transports: sockJsProtocols }));
    yield call(stompConnectWithPromise, stompClient, socketState);
    yield all(map(startListener, [apiListener, chatListener, eventListener]));
    yield put(socketActions.socketConnectedSuccess());

    const { isWebappUser } = action.payload;
    if (isWebappUser) {
      store.dispatch({
        type: types.IDENTITY_IS_WEBAPP_USER,
        payload: true,
      });
    }

    /**
     * Fetching all chats in getAppStartData, called from:
     *    1) main index.js - initial load of the application
     *    2) stompClient.connect where socketStatus is not unestablished or connected (via syncData)
     * creates the channel subscriptions necessary. Leaving this function/comment in here just in case there is a problem.
     */
    // yield call(subscribeToChannels);
    reconnectAttempts = 0;

    // TODO: Change this action name to "clear...." and remove the boolean
    store.dispatch(socketActions.maximumSocketReconnectAttemptsReached(false));
  } catch (error) {
    logError('Failed socket connection.', error);
    yield put(socketActions.socketConnectFailed());
  }
}

/**
 * Initiates all connections, starts the event listener, and grabs startup data
 *
 * return - number represents generally the http status of the request
 */
export default function* appStart() {
  try {
    // Required for downstream parts of the application, block until we have this information
    let identity;
    try {
      const isAuthed = yield call(sendSagas.isAuthenticated);

      if (!isAuthed) {
        console.error('user isn\'t authed');
        // user isn't authenticated, kick them out to login
        window.location.replace(endpoints.MEMBER_LOGIN);
        return HttpStatus.UNAUTHORIZED;
      }

      identity = yield call(sendSagas.getIdentity);
      setAuth(LOGGED_IN);
    } catch (e) {
      if ([HttpStatus.FORBIDDEN, HttpStatus.UNAUTHORIZED].includes(get(e, 'response.status'))) {
        window.location.replace(endpoints.MEMBER_LOGIN);
        return HttpStatus.UNAUTHORIZED;
      } else {
        logError('Failed to getIdentity', e);
        return HttpStatus.INTERNAL_SERVER_ERROR;
      }
    }

    // Check if the window is an iFrame or not (Inbox Messenger uses an iFrame)
    const isIFrame = window !== window.parent;

    /** This check is for Inbox-Messenger only */
    const { memberId } = identity.data;
    if (isIFrame && !isSameMember(memberId)) {
      logWarning(`Inbox Messenger member [${getLocalMemberId()}] does not match the logged in Messenger member [${memberId}], sending user to log out and back in`);
      window.location.replace(endpoints.LOCAL_LOGOUT);
      return HttpStatus.UNAUTHORIZED;
    }

    addUserToSentryScope(identity.data.user);

    yield put(actions.configureSentry(get(identity, 'data.user')));

    yield call(socketConnect, socketActions.socketConnect(isIFrame));
    yield spawn(rootListener);
  } catch (error) {
    logError('Error starting app', error);
    return HttpStatus.INTERNAL_SERVER_ERROR;
  }
}
